
import {
  computed, defineComponent, PropType, ref, shallowRef, watch, provide, onMounted, onBeforeUnmount, nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { cloneDeep, isEqual } from 'lodash';
import Sidebar from 'primevue/sidebar';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import TabMenu from 'primevue/tabmenu';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import FlowOperatorForm from './FlowOperatorForm.vue';
import type { FlowOperatorMetadata } from '@/types/flow-types';
import type { FlowStructureDto } from '@/types/flow-structure';
import {
  getCompatibleOperators,
  getIncompatibleEventValues,
  getOperatorById,
  groupOperatorsByKind,
  OperatorInsertContext,
} from './operators/registry';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { useServiceVerification } from '@/composables/flows/useServiceVerification';
import { getFeatureFlagForOperator } from '@/configs/feature-flags';
import ComingSoonBadge from '@/components/shared/ComingSoonBadge.vue';

type PanelMode = 'list' | 'form';

interface OperatorFormInstance {
  $el: HTMLElement;
  handleSubmit: () => Promise<void>;
  settlePendingInput: () => Promise<void>;
  isBusy: () => boolean;
}

export default defineComponent({
  name: 'FlowOperatorPanel',

  components: {
    Sidebar,
    Button,
    InputText,
    TabMenu,
    Dialog,
    SpmButton,
    FlowOperatorForm,
    ComingSoonBadge,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    context: {
      type: Object as PropType<OperatorInsertContext | null>,
      default: null,
    },
    editStep: {
      type: Object as PropType<{ id: string; type: string; parameters: Record<string, any> } | null>,
      default: null,
    },
    replaceStep: {
      type: Object as PropType<{ id: string; type: string } | null>,
      default: null,
    },
    flow: {
      type: Object as PropType<FlowStructureDto | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'add', 'save', 'replace', 'decrease-branches', 'update-step'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Provide flow and context to child components (for LinkedOperatorSelector)
    const flowRef = computed(() => props.flow);
    const contextRef = computed(() => props.context);
    provide('flow', flowRef);
    provide('insertContext', contextRef);
    // Events incompatible as a child of the insertion parent — the internal-event
    // form hides them from its event picker (clean UX vs. post-hoc warning).
    const forbiddenEventValues = computed(() => getIncompatibleEventValues(props.context));
    provide('forbiddenEventValues', forbiddenEventValues);

    // State for hiding panel during selection mode (without losing form state)
    const isHiddenForSelection = ref(false);

    // Provide reopen panel callback for LinkedOperatorSelector
    const reopenPanel = () => {
      isHiddenForSelection.value = false;
    };
    provide('reopenPanel', reopenPanel);

    // Handle start-selection event from LinkedOperatorSelector
    const handleStartSelection = () => {
      // Hide the panel visually when entering selection mode (but keep state)
      isHiddenForSelection.value = true;
    };
    provide('handleStartSelection', handleStartSelection);

    const searchQuery = ref('');
    const activeTab = ref(0);
    const mode = ref<PanelMode>('list');
    const selectedOperator = shallowRef<FlowOperatorMetadata | null>(null);
    const formData = ref<any>(null);
    const operatorFormRef = ref<OperatorFormInstance | null>(null);

    // Copy of the form data taken on the user's first interaction with the form, not on open:
    // what the form fills in by itself while mounting (default sender, type casts…) must not
    // count as an edit. Null until the user has interacted with the current form.
    let pristineData: Record<string, unknown> | null = null;

    // Provide updateStep callback so composables can persist data without user clicking save.
    // Also used by the form itself (WhatsApp "Submit for validation"). What is saved this way is no
    // longer an unsaved change: the copy of the form takes it too.
    const updateStep = (payload: { stepId: string; data: Record<string, any> }) => {
      if (pristineData !== null) {
        pristineData = { ...pristineData, ...cloneDeep(payload.data) };
      }
      emit('update-step', payload);
    };
    provide('updateStep', updateStep);

    // Unsaved changes dialog, and the close action it is about
    const isUnsavedChangesDialogVisible = ref(false);
    let pendingDiscard: (() => void) | null = null;
    // "Save" clicked in the dialog: it stays open, showing the save in progress, until the panel closes
    const isSavingFromDialog = ref(false);

    // Bumped whenever the panel opens, closes or switches form: a close request that resolves
    // after that is about another form and is dropped
    let formSession = 0;

    const resetFormSession = () => {
      pristineData = null;
      isUnsavedChangesDialogVisible.value = false;
      pendingDiscard = null;
      formSession += 1;
    };

    const markFirstInteraction = () => {
      if (pristineData === null) {
        pristineData = cloneDeep(formData.value);
      }
    };

    // Listened to on the whole page: the first interaction can happen outside the form, in the
    // template selector or a dropdown list, which are rendered at the end of the page. The mask
    // around the panel does not count: the second click of a double click on a step lands on it.
    const onDocumentInteraction = (event: Event) => {
      const target = event.target as HTMLElement;
      if (props.visible && mode.value === 'form' && !target.classList?.contains('p-sidebar-mask')) {
        markFirstInteraction();
      }
    };

    onMounted(() => {
      document.addEventListener('pointerdown', onDocumentInteraction, true);
      document.addEventListener('keydown', onDocumentInteraction, true);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('pointerdown', onDocumentInteraction, true);
      document.removeEventListener('keydown', onDocumentInteraction, true);
    });

    // Compares the data in the shape the operator stores it, so that a field the form
    // re-emits with another type or key order is not taken for a change.
    const normalizeFormData = (data: Record<string, unknown>) => {
      const operator = selectedOperator.value;
      return operator ? operator.defaultData({ ...data }, props.context?.campaignType) : data;
    };

    const hasUnsavedChanges = () => mode.value === 'form'
      && pristineData !== null
      && !isEqual(normalizeFormData(formData.value), normalizeFormData(pristineData));

    const compatibleOperators = computed(() => {
      if (!props.context) return [];
      return getCompatibleOperators(props.context);
    });

    const groupedOperators = computed(() => {
      return groupOperatorsByKind(compatibleOperators.value);
    });

    const filterBySearch = (operators: FlowOperatorMetadata[]) => {
      if (!searchQuery.value.trim()) return operators;
      const query = searchQuery.value.toLowerCase();
      return operators.filter(op => {
        const label = t(op.label).toLowerCase();
        return label.includes(query);
      });
    };

    const filteredTriggers = computed(() => filterBySearch(groupedOperators.value.triggers));
    const filteredActions = computed(() => filterBySearch(groupedOperators.value.actions));
    const filteredFilters = computed(() => filterBySearch(groupedOperators.value.filters));

    const filteredOperators = computed(() => [
      ...filteredTriggers.value,
      ...filteredActions.value,
      ...filteredFilters.value,
    ]);

    const tabItems = computed(() => {
      const items: { label: string; key: string }[] = [];
      if (groupedOperators.value.triggers.length > 0) {
        items.push({ label: t('flows.operatorKinds.trigger'), key: 'triggers' });
      }
      if (groupedOperators.value.actions.length > 0) {
        items.push({ label: t('flows.operatorKinds.action'), key: 'actions' });
      }
      if (groupedOperators.value.filters.length > 0) {
        items.push({ label: t('flows.operatorKinds.filter'), key: 'filters' });
      }
      return items;
    });

    const currentOperators = computed(() => {
      const key = tabItems.value[activeTab.value]?.key;
      if (key === 'triggers') return filteredTriggers.value;
      if (key === 'actions') return filteredActions.value;
      if (key === 'filters') return filteredFilters.value;
      return [];
    });

    // Service verification
    const { verifyAndShowModalIfNeeded } = useServiceVerification();

    const openOperatorForm = (operator: FlowOperatorMetadata) => {
      resetFormSession();
      selectedOperator.value = operator;
      formData.value = operator.defaultData(undefined, props.context?.campaignType);
      mode.value = 'form';
    };

    const selectOperator = async (operator: FlowOperatorMetadata) => {
      // Verify services before opening the form
      const canProceed = await verifyAndShowModalIfNeeded(
        operator.id,
        () => openOperatorForm(operator),
      );

      if (canProceed) {
        openOperatorForm(operator);
      }
    };

    const backToList = () => {
      // In edit mode, close the panel instead of going back to list
      if (isEditMode.value) {
        emit('update:visible', false);
        return;
      }
      resetFormSession();
      mode.value = 'list';
      selectedOperator.value = null;
      formData.value = null;
    };

    const handleClose = () => {
      emit('update:visible', false);
    };

    // Element that had the focus when the dialog opened. Closing the dialog removes the button that had
    // the focus then: without giving it back to the form, the next Escape would not reach the panel.
    let focusBeforeDialog: Element | null = null;

    const restoreFocusAfterDialog = () => {
      const previousFocus = focusBeforeDialog;
      focusBeforeDialog = null;
      // Nothing to do when the dialog closed the form
      const form = operatorFormRef.value?.$el;
      if (!form || !props.visible || mode.value !== 'form') {
        return;
      }
      const target = previousFocus instanceof HTMLElement && form.contains(previousFocus)
        ? previousFocus
        : form.querySelector('button');
      if (target) {
        target.focus({ preventScroll: true });
      }
    };

    // Closing the form (cross, Cancel, Escape) used to drop its changes silently: ask first.
    // Closing the dialog itself (cross, Escape) keeps the user on the form.
    const confirmBeforeDiscarding = async (discard: () => void) => {
      // Escape pressed while the dialog is open, with the focus left in the form, reaches the panel
      // too: the dialog closes itself and must not be reopened
      if (isUnsavedChangesDialogVisible.value) {
        return;
      }
      const session = formSession;
      // Let the key press that asked for the close finish first: the dialog listens to Escape on the
      // document as soon as it opens, and would catch that same key press and close straight away
      await new Promise((resolve) => { setTimeout(resolve); });
      // A value typed just before closing may not have reached formData yet
      if (operatorFormRef.value) {
        await operatorFormRef.value.settlePendingInput();
      }
      // While the form saves or waits for WhatsApp, its save button is disabled: the panel does not
      // close either, nor offer to save a second time from the dialog
      if (session !== formSession || operatorFormRef.value?.isBusy()) {
        return;
      }
      if (!hasUnsavedChanges()) {
        discard();
        return;
      }
      pendingDiscard = discard;
      focusBeforeDialog = document.activeElement;
      isSavingFromDialog.value = false;
      isUnsavedChangesDialogVisible.value = true;
    };

    const discardChanges = () => {
      if (isSavingFromDialog.value) {
        return;
      }
      isUnsavedChangesDialogVisible.value = false;
      if (pendingDiscard) {
        pendingDiscard();
      }
      pendingDiscard = null;
    };

    // Goes through the form's own submit: its validation applies. When it succeeds the panel closes,
    // taking the dialog with it; when it fails, the dialog closes and the form shows the errors.
    const saveChanges = async () => {
      if (!operatorFormRef.value || isSavingFromDialog.value) {
        return;
      }
      const session = formSession;
      pendingDiscard = null;
      isSavingFromDialog.value = true;
      try {
        await operatorFormRef.value.handleSubmit();
      } finally {
        // Lets the panel close first when the save went through
        await nextTick();
        if (session === formSession) {
          isSavingFromDialog.value = false;
          isUnsavedChangesDialogVisible.value = false;
        }
      }
    };

    const requestClose = () => confirmBeforeDiscarding(handleClose);
    const requestCancel = () => confirmBeforeDiscarding(backToList);

    // Dropdowns and autocompletes mark every Escape as handled, even when their list is closed:
    // read their state before they react, so that this key press can still close the panel
    let isEscapeOnClosedList = false;

    const onFormKeydownCapture = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      isEscapeOnClosedList = event.code === 'Escape'
        && target.getAttribute('role') === 'combobox'
        && target.getAttribute('aria-expanded') !== 'true';
    };

    // A field that handles Escape itself (e.g. a dropdown closing its list) marks the event as
    // handled: that key press must not also close the whole panel.
    const stopEscapeHandledByField = (event: KeyboardEvent) => {
      if (event.code === 'Escape' && event.defaultPrevented && !isEscapeOnClosedList) {
        event.stopPropagation();
      }
    };

    const isEditMode = computed(() => !!props.editStep);
    const isReplaceMode = computed(() => !!props.replaceStep);

    const confirmAdd = (data?: any) => {
      if (selectedOperator.value) {
        let finalData = data ?? formData.value;

        // Handle branch decrease in edit mode before cleaning data
        if (isEditMode.value && props.editStep) {
          const initialOutputs = finalData.initial_number_outputs;
          const outputsToDelete = finalData.outputs_to_delete;
          const newOutputs = selectedOperator.value.outputs?.(finalData) ?? 1;

          console.log('[FlowOperatorPanel] Branch decrease check:', {
            initialOutputs,
            newOutputs,
            outputsToDelete,
            finalData,
          });

          // For ABTest: use outputs_to_delete array
          if (outputsToDelete && Array.isArray(outputsToDelete) && outputsToDelete.length > 0) {
            console.log('[FlowOperatorPanel] Emitting decrease-branches for ABTest:', outputsToDelete);
            emit('decrease-branches', {
              stepId: props.editStep.id,
              outputsToRemove: outputsToDelete,
            });
          }
          // For filters: if outputs decreased from 2 to 1, remove output_2
          else if (initialOutputs !== undefined && initialOutputs !== null && newOutputs < initialOutputs) {
            console.log('[FlowOperatorPanel] Emitting decrease-branches for filter');
            emit('decrease-branches', {
              stepId: props.editStep.id,
              outputsToRemove: ['output_2'],
            });
          }
        }

        // Call beforeInsert if defined to transform data before saving
        if (selectedOperator.value.beforeInsert) {
          finalData = selectedOperator.value.beforeInsert(finalData);
        }

        if (isEditMode.value && props.editStep) {
          emit('save', {
            stepId: props.editStep.id,
            data: finalData,
          });
        } else if (isReplaceMode.value && props.replaceStep) {
          emit('replace', {
            operator: selectedOperator.value,
            data: finalData,
          });
        } else {
          emit('add', {
            operator: selectedOperator.value,
            data: finalData,
          });
        }
        emit('update:visible', false);
      }
    };


    watch([() => props.visible, () => props.editStep, () => props.replaceStep, () => props.context], ([newVisible, newEditStep, newReplaceStep, newContext]) => {
      // Opening, closing or switching step starts over: no snapshot, no dialog, no pending close
      resetFormSession();
      if (newVisible) {
        if (newEditStep) {
          // Edit mode: open form directly with existing data
          const operator = getOperatorById(newEditStep.type);
          if (operator) {
            selectedOperator.value = operator;
            // Parse parameters back to form data format
            const parsedParams = parseOperatorParameters(newEditStep.parameters);
            const baseData = { ...operator.defaultData(undefined, props.context?.campaignType), ...parsedParams };

            // Store initial number of outputs for tracking changes
            if (operator.outputs) {
              baseData.initial_number_outputs = operator.outputs(baseData);
            }

            formData.value = baseData;
            mode.value = 'form';
          }
        } else if (newReplaceStep) {
          // Replace mode: show operator list to select replacement
          searchQuery.value = '';
          activeTab.value = 0;
          mode.value = 'list';
          selectedOperator.value = null;
          formData.value = null;
        } else if (newContext?.preSelectedOperator) {
          // Pre-selected operator: go directly to form (for bulk campaigns empty state)
          selectedOperator.value = newContext.preSelectedOperator;
          formData.value = newContext.preSelectedOperator.defaultData(undefined, newContext.campaignType);
          mode.value = 'form';
        } else {
          // Add mode: show operator list
          searchQuery.value = '';
          activeTab.value = 0;
          mode.value = 'list';
          selectedOperator.value = null;
          formData.value = null;
        }
      }
    }, { immediate: true });

    const getOperatorFlag = getFeatureFlagForOperator;

    return {
      t,
      searchQuery,
      getOperatorFlag,
      activeTab,
      mode,
      selectedOperator,
      formData,
      groupedOperators,
      filteredTriggers,
      filteredActions,
      filteredFilters,
      filteredOperators,
      tabItems,
      currentOperators,
      selectOperator,
      handleClose,
      confirmAdd,
      isEditMode,
      isReplaceMode,
      isHiddenForSelection,
      operatorFormRef,
      updateStep,
      requestClose,
      requestCancel,
      onFormKeydownCapture,
      stopEscapeHandledByField,
      isUnsavedChangesDialogVisible,
      isSavingFromDialog,
      restoreFocusAfterDialog,
      discardChanges,
      saveChanges,
    };
  },
});
